import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import {
  Activity,
  DollarSign,
  Cpu,
  HardDrive,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Server,
  Target,
  Gauge
} from 'lucide-react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { useMonitoring } from '../context/MonitoringContext';

import { parseUtc } from '../lib/parseUtc';
const EnhancedClusterOverview = ({ clusterId }) => {
  const [clusterData, setClusterData] = useState(null);
  const [historicalTrend, setHistoricalTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);

  // Prefer the MonitoringContext snapshot so values match the page header
  // and every other Monitoring tab. Falls back to a local fetch only when
  // the component is mounted outside the provider (defensive).
  let contextData = null;
  try {
    contextData = useMonitoring();
  } catch (e) {
    contextData = null;
  }
  const [localRealTimeMetrics, setLocalRealTimeMetrics] = useState(null);

  // The rest of this component reads `realTimeMetrics` — derive it from
  // context first, then fall back to local fetch. Shape is the original
  // /realtime-metrics response, so downstream rendering needs no change.
  const realTimeMetrics = contextData?.realTimeData || localRealTimeMetrics;

  useEffect(() => {
    if (clusterId) {
      fetchClusterOverview();
      // Only spin a local poller if we don't have context (defensive — in
      // practice this component is always rendered inside MonitoringProvider).
      if (!contextData) {
        intervalRef.current = setInterval(fetchClusterOverview, 30000);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [clusterId, contextData]);

  const fetchClusterOverview = async () => {
    try {
      // Cluster meta + 24h trend chart are independent of the live snapshot,
      // so we still fetch them locally. The realtime card values flow from
      // context above.
      const clusterRes = await axios.get(`${API}/clusters`)
        .then((res) => res.data.find((c) => c.id === clusterId) || {});
      const trendRes = await axios.get(`${API}/agent-metrics/${clusterId}?limit=24`);

      setClusterData(clusterRes);

      // Local realtime fallback — only used when no MonitoringContext.
      if (!contextData) {
        try {
          const metricsRes = await axios.get(`${API}/realtime-metrics/${clusterId}`);
          setLocalRealTimeMetrics(metricsRes.data);
        } catch (_) { /* swallowed; UI shows empty */ }
      }

      const trendData = trendRes.data.slice(-24).map(metric => ({
        timestamp: parseUtc(metric.timestamp).getHours() + ':00',
        cost: metric.cost_estimate,
        cpu_usage: metric.cpu_usage,
        memory_usage: metric.memory_usage,
        efficiency: metric.efficiency_metrics?.overall_efficiency || 0
      }));
      setHistoricalTrend(trendData);

      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch cluster overview');
      toast.error('Failed to fetch cluster overview');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    }).format(amount);
  };

  const getHealthStatus = (efficiency) => {
    if (efficiency >= 80) return { status: 'Excellent', color: 'text-green-600', bgColor: 'bg-green-100' };
    if (efficiency >= 60) return { status: 'Good', color: 'text-yellow-600', bgColor: 'bg-yellow-100' };
    if (efficiency >= 40) return { status: 'Fair', color: 'text-orange-600', bgColor: 'bg-orange-100' };
    return { status: 'Poor', color: 'text-red-600', bgColor: 'bg-red-100' };
  };

  const getCostTrend = () => {
    if (historicalTrend.length < 2) return { trend: 'stable', icon: Activity };
    
    const recent = historicalTrend.slice(-3).reduce((sum, item) => sum + item.cost, 0) / 3;
    const previous = historicalTrend.slice(-6, -3).reduce((sum, item) => sum + item.cost, 0) / 3;
    
    if (recent > previous * 1.1) return { trend: 'increasing', icon: TrendingUp, color: 'text-red-600' };
    if (recent < previous * 0.9) return { trend: 'decreasing', icon: TrendingDown, color: 'text-green-600' };
    return { trend: 'stable', icon: Activity, color: 'text-blue-600' };
  };

  const CHART_COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b'];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="pb-2">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              </CardHeader>
              <CardContent>
                <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-full"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200">
        <CardContent className="pt-6">
          <div className="flex items-center space-x-2 text-red-600">
            <AlertTriangle className="h-5 w-5" />
            <span>{error}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!realTimeMetrics || !clusterData) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-gray-500">No cluster data available</p>
        </CardContent>
      </Card>
    );
  }

  const health = getHealthStatus(realTimeMetrics.efficiency_metrics?.overall_efficiency || 0);
  const costTrend = getCostTrend();
  const TrendIcon = costTrend.icon;

  return (
    <div className="space-y-6">
      {/* Cluster Health Header */}
      <Card className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark shadow-sm">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-50 dark:bg-blue-500/10 rounded-md">
                <Server className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{clusterData.name}</h2>
                <p className="text-xs text-slate-500 dark:text-text-secondary">Cluster Overview</p>
              </div>
            </div>
            <div className="text-right">
              <Badge className={`${health.bgColor} ${health.color} border-0 text-xs font-medium`}>
                {health.status}
              </Badge>
              <p className="text-xs text-slate-500 dark:text-text-secondary mt-1">
                Last sync: {parseUtc(clusterData.last_sync).toLocaleString()}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Real-time Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Cost per Hour */}
        <Card className="hover:shadow-lg transition-shadow dark:bg-surface-dark dark:border-surface-border">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium dark:text-text-secondary">Cost per Hour</CardTitle>
            <div className="flex items-center space-x-1">
              <DollarSign className="h-4 w-4 text-muted-foreground" />
              <TrendIcon className={`h-3 w-3 ${costTrend.color}`} />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold dark:text-white">{formatCurrency(realTimeMetrics.cost_per_hour)}</div>
            <p className="text-xs text-muted-foreground dark:text-text-secondary">
              {formatCurrency(realTimeMetrics.cost_per_hour * 24)}/day projected
            </p>
          </CardContent>
        </Card>

        {/* Resource Utilization — distinct from Dashboard's cost-based
            Efficiency Score. Headroom is healthy; do not equate. */}
        <Card className="hover:shadow-lg transition-shadow dark:bg-surface-dark dark:border-surface-border">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium dark:text-text-secondary">Resource Utilization</CardTitle>
            <Gauge className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${health.color}`}>
              {(realTimeMetrics.efficiency_metrics?.overall_efficiency || 0).toFixed(1)}%
            </div>
            <Progress
              value={realTimeMetrics.efficiency_metrics?.overall_efficiency || 0}
              className="mt-2"
            />
          </CardContent>
        </Card>

        {/* Active Resources */}
        <Card className="hover:shadow-lg transition-shadow dark:bg-surface-dark dark:border-surface-border">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium dark:text-text-secondary">Active Resources</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold dark:text-white">{realTimeMetrics.pods_count}</div>
            <p className="text-xs text-muted-foreground dark:text-text-secondary">
              {realTimeMetrics.nodes_count} nodes • {realTimeMetrics.pods_count} pods
            </p>
          </CardContent>
        </Card>

        {/* Active Alerts */}
        <Card className="hover:shadow-lg transition-shadow dark:bg-surface-dark dark:border-surface-border">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium dark:text-text-secondary">Active Alerts</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">
              {realTimeMetrics.alerts?.length || 0}
            </div>
            <p className="text-xs text-muted-foreground dark:text-text-secondary">
              {realTimeMetrics.alerts?.filter(a => a.severity === 'critical').length || 0} critical
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Resource Utilization Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* CPU & Memory Usage */}
        <Card className="dark:bg-surface-dark dark:border-surface-border">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 dark:text-white">
              <Cpu className="h-5 w-5" />
              <span>Resource Utilization</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium dark:text-text-secondary">CPU Usage</span>
                <span className="text-sm font-bold dark:text-white">
                  {(realTimeMetrics.efficiency_metrics?.cpu_efficiency || 0).toFixed(1)}%
                </span>
              </div>
              <Progress 
                value={realTimeMetrics.efficiency_metrics?.cpu_efficiency || 0}
                className="h-2"
              />
            </div>
            
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium dark:text-text-secondary">Memory Usage</span>
                <span className="text-sm font-bold dark:text-white">
                  {(realTimeMetrics.efficiency_metrics?.memory_efficiency || 0).toFixed(1)}%
                </span>
              </div>
              <Progress 
                value={realTimeMetrics.efficiency_metrics?.memory_efficiency || 0}
                className="h-2"
              />
            </div>

            <div className="pt-2 border-t dark:border-surface-border">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground dark:text-text-secondary">Overall Efficiency</span>
                <span className={`font-bold ${health.color}`}>
                  {(realTimeMetrics.efficiency_metrics?.overall_efficiency || 0).toFixed(1)}%
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Cost Trend Chart */}
        <Card className="dark:bg-surface-dark dark:border-surface-border">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 dark:text-white">
              <TrendingUp className="h-5 w-5" />
              <span>24-Hour Cost Trend</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {historicalTrend.length > 0 ? (
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={historicalTrend}>
                    <defs>
                      <linearGradient id="costGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="timestamp" stroke="#64748b" fontSize={12} />
                    <YAxis stroke="#64748b" fontSize={12} />
                    <Tooltip 
                      formatter={(value) => [formatCurrency(value), 'Cost/Hour']}
                      contentStyle={{ 
                        backgroundColor: 'rgba(15, 23, 42, 0.96)', 
                        border: 'none',
                        borderRadius: '8px',
                        color: '#f1f5f9'
                      }}
                      labelStyle={{ color: '#f1f5f9' }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="cost" 
                      stroke="#3b82f6" 
                      strokeWidth={2}
                      fill="url(#costGradient)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-48 flex items-center justify-center text-muted-foreground dark:text-text-secondary">
                No trend data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Per-Node Cost Breakdown */}
      {realTimeMetrics.node_costs && realTimeMetrics.node_costs.length > 0 && (
        <Card className="dark:bg-surface-dark dark:border-surface-border">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 dark:text-white">
              <Server className="h-5 w-5" />
              <span>Per-Node Cost</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {realTimeMetrics.node_costs.map((node, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-surface-border rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-sm dark:text-white">{node.name}</span>
                      <Badge variant="outline" className="text-xs">{node.instance_type}</Badge>
                      <Badge variant="outline" className="text-xs">{node.provider}</Badge>
                    </div>
                    <span className="text-xs text-muted-foreground dark:text-text-secondary">{node.region}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold dark:text-white">{formatCurrency(node.hourly_cost)}/hr</div>
                    <div className="text-xs text-muted-foreground dark:text-text-secondary">{formatCurrency(node.daily_cost)}/day</div>
                  </div>
                </div>
              ))}
              <div className="flex justify-between items-center pt-3 border-t dark:border-surface-border">
                <span className="text-sm font-medium dark:text-text-secondary">Total</span>
                <div className="text-right">
                  <span className="text-sm font-bold dark:text-white">
                    {formatCurrency(realTimeMetrics.node_costs.reduce((sum, n) => sum + (n.hourly_cost || 0), 0))}/hr
                  </span>
                  <span className="text-xs text-muted-foreground dark:text-text-secondary ml-2">
                    ({formatCurrency(realTimeMetrics.node_costs.reduce((sum, n) => sum + (n.monthly_cost || 0), 0))}/mo)
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Live Resource Snapshot — real signals only, no synthetic numbers */}
      <Card className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark shadow-sm">
        <CardHeader className="border-b border-slate-100 dark:border-surface-border pb-3">
          <CardTitle className="text-sm font-semibold flex items-center space-x-2 dark:text-white">
            <Target className="h-4 w-4 text-slate-500" />
            <span>Live Resource Snapshot</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="border border-slate-200 dark:border-surface-border rounded-md p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-text-secondary">CPU Utilization</p>
                <div className="p-1.5 bg-violet-50 dark:bg-violet-500/10 rounded-md">
                  <Cpu className="w-3.5 h-3.5 text-violet-600 dark:text-violet-400" />
                </div>
              </div>
              <p className="text-2xl font-semibold text-slate-900 dark:text-white tabular-nums">
                {(realTimeMetrics.efficiency_metrics?.cpu_efficiency || 0).toFixed(1)}<span className="text-base text-slate-400 dark:text-slate-500 font-normal ml-0.5">%</span>
              </p>
              <p className="text-xs text-slate-500 dark:text-text-secondary mt-1">of allocatable CPU</p>
            </div>

            <div className="border border-slate-200 dark:border-surface-border rounded-md p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-text-secondary">Memory Utilization</p>
                <div className="p-1.5 bg-rose-50 dark:bg-rose-500/10 rounded-md">
                  <HardDrive className="w-3.5 h-3.5 text-rose-600 dark:text-rose-400" />
                </div>
              </div>
              <p className="text-2xl font-semibold text-slate-900 dark:text-white tabular-nums">
                {(realTimeMetrics.efficiency_metrics?.memory_efficiency || 0).toFixed(1)}<span className="text-base text-slate-400 dark:text-slate-500 font-normal ml-0.5">%</span>
              </p>
              <p className="text-xs text-slate-500 dark:text-text-secondary mt-1">of allocatable memory</p>
            </div>

            <div className="border border-slate-200 dark:border-surface-border rounded-md p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-text-secondary">Daily Cost</p>
                <div className="p-1.5 bg-emerald-50 dark:bg-emerald-500/10 rounded-md">
                  <DollarSign className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                </div>
              </div>
              <p className="text-2xl font-semibold text-slate-900 dark:text-white tabular-nums">
                <span className="text-base text-slate-400 dark:text-slate-500 font-normal">$</span>{((realTimeMetrics.cost_per_hour || 0) * 24).toFixed(2)}
              </p>
              <p className="text-xs text-slate-500 dark:text-text-secondary mt-1">at current hourly rate</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Last Updated */}
      <div className="text-xs text-gray-500 dark:text-text-secondary text-center">
        Last updated: {parseUtc(realTimeMetrics.timestamp).toLocaleString()}
      </div>
    </div>
  );
};

export default EnhancedClusterOverview;